<template>
  <div
    class="mac-desktop w-screen h-screen relative overflow-hidden font-pixel flex flex-col"
    :style="{ backgroundColor: '#4aa3a3', backgroundImage: `url(${desktopBackground})`, backgroundRepeat: 'repeat' }"
  >
    <!-- Menu bar -->
    <div class="mac-menu-bar flex-shrink-0 flex items-center h-10 px-1 w-full border-b-2 border-black bg-gray-300 shadow-pixel-sm">
      <button type="button" class="mac-menu-apple h-10 w-10 flex items-center justify-center p-0.5 border border-transparent hover:border-black flex-shrink-0">
        <img :src="appleIcon" alt="Apple" class="h-full w-full object-contain">
      </button>
      <div class="ml-2 flex items-center gap-1">
        <div ref="skillsMenuRef" class="relative" @click.stop>
          <button
            type="button"
            class="mac-menu-item px-2 py-1 font-pixel text-xl font-medium text-black border border-transparent hover:border-black"
            :class="{ 'border-black bg-gray-400': skillsDropdownOpen }"
            @click="skillsDropdownOpen = !skillsDropdownOpen"
          >
            Skills
          </button>
          <div
            v-show="skillsDropdownOpen"
            class="mac-menu-dropdown absolute left-0 top-full mt-0.5 border-2 border-black bg-white shadow-pixel min-w-[140px] py-1 z-50"
          >
            <div
              v-for="skill in skillsList"
              :key="skill"
              class="px-3 py-1.5 font-pixel text-xl font-medium text-black hover:bg-gray-200 cursor-default"
            >
              {{ skill }}
            </div>
          </div>
        </div>
        <button
          type="button"
          class="mac-menu-item px-2 py-1 font-pixel text-xl font-medium text-black border border-transparent hover:border-black"
          @click="openWindow('about'); skillsDropdownOpen = false"
        >
          About
        </button>
        <button
          type="button"
          class="mac-menu-item px-2 py-1 font-pixel text-xl font-medium text-black border border-transparent hover:border-black"
          @click="openWindow('work'); skillsDropdownOpen = false"
        >
          Work
        </button>
      </div>

      <!-- Time / Date - top right (date to the right of time) -->
      <div class="ml-auto mr-1 flex flex-row items-center gap-2 leading-none">
        <div class="font-pixel text-xl font-medium text-black">
          {{ timeText }}
        </div>
        <div class="font-pixel text-xl font-medium text-black opacity-90 whitespace-nowrap">
          {{ dateText }}
        </div>
      </div>
    </div>

    <!-- Desktop content -->
    <div class="flex-1 relative overflow-hidden">
    <!-- Folder icons -->
    <div class="absolute flex flex-col gap-8" style="left: 32px; top: 24px;">
      <div style="margin-top: 50px;">
        <FolderIcon label="Intro" :icon="fileIcon" @open="openWindow('intro')" />
      </div>
      <FolderIcon label="About" :icon="fileIcon" @open="openWindow('about')" />
      <FolderIcon label="Work" @open="openWindow('work')" />
      <FolderIcon label="Contact" :icon="fileIcon" @open="openWindow('contact')" />
      <FolderIcon label="Calculator" :icon="calculatorIcon" single-line @open="openWindow('calculator')" />
    </div>

    <!-- Macintosh HD - top right -->
    <div class="absolute flex flex-col items-center" style="right: 32px; top: 32px;">
      <div class="mac-hd-icon w-16 h-12 border-2 border-black bg-white shadow-pixel-sm flex items-center pl-2">
        <div class="w-1.5 h-2 bg-black" />
      </div>
      <div class="mt-1 px-2 py-0.5 border border-black bg-white">
        <span class="font-pixel text-base font-medium text-black">Macintosh HD</span>
      </div>
    </div>

    <!-- EatWeat Folder - right side below Macintosh HD -->
      
      <div
      class="absolute"
      style="right: 32px; top: 200px;"
      >
      <FolderIcon
        label="My Personal Project: EatWeat"
        @open="openEatWeat"
      />
      </div>

    <!-- Trash - bottom right -->
    <div class="absolute flex flex-col items-center" style="right: 32px; bottom: 32px;">
      <img
        :src="trashIcon"
        alt="Trash"
        class="w-12 h-14 object-contain"
      >
      <div class="mt-1 px-2 py-0.5 border border-black bg-white">
        <span class="font-pixel text-base font-medium text-black">Trash</span>
      </div>
    </div>

    <!-- Windows -->
    <Window
      v-for="win in openWindows"
      :key="win.id"
      :id="win.id"
      :title="win.title"
      :z-index="win.zIndex"
      :initial-position="win.position"
      :width="win.width"
      :min-height="win.minHeight"
      @close="closeWindow(win.id)"
    >
      <!-- Intro -->
      <div v-if="win.id === 'intro'" class="intro-content text-center py-4 sm:py-6 px-2">
        <h1 class="font-pixel font-bold text-black mb-2 leading-none text-[clamp(2.25rem,12vw,9.375rem)]">
          Saad Ilahi
        </h1>
        <p class="font-pixel text-black text-xl sm:text-2xl md:text-3xl px-1">Full Stack Developer & Project Manager</p>
        <p class="font-pixel text-black mt-4 sm:mt-6 opacity-90 text-base sm:text-xl md:text-2xl px-1">
          (This experience is inspired by retro Mac OS — you can interact with it similarly.)
        </p>
      </div>
      <!-- About -->
      <div v-else-if="win.id === 'about'" class="about-content py-2 px-1 sm:px-0">
        <p class="font-pixel text-black mb-4 leading-relaxed text-lg sm:text-[25px]">
          I build web and mobile applications with a focus on clean architecture and user experience.
          I enjoy turning ideas into working products and leading teams to ship on time.
        </p>
        <h2 class="font-pixel text-lg sm:text-[25px] font-bold text-black mb-2 border-b border-black pb-1">Skills</h2>
        <ul class="font-pixel text-lg sm:text-[25px] text-black grid grid-cols-1 min-[420px]:grid-cols-2 gap-x-4 gap-y-1">
          <li v-for="skill in skillsList" :key="skill" class="break-words">• {{ skill }}</li>
        </ul>
      </div>
      <!-- Work (folder picker: Websites / Mobile apps on the left, same as desktop) -->
      <div v-else-if="win.id === 'work'" class="work-content py-6 px-2 sm:px-4">
        <div class="flex flex-row flex-wrap gap-8 sm:gap-12 justify-start">
          <FolderIcon
            label="Websites"
            :open-on-single-click="true"
            @open="openWindow('work-websites')"
          />
          <FolderIcon
            label="Mobile apps"
            :open-on-single-click="true"
            @open="openWindow('work-mobile')"
          />
        </div>
      </div>
      <!-- Work - Websites (fixed height, scrollable project list) -->
      <div v-else-if="win.id === 'work-websites'" class="work-projects-content min-h-0">
        <div class="overflow-y-auto py-2 pr-1 h-[min(576px,calc(100dvh-10rem))]">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              v-for="project in websiteProjects"
              :key="project.id"
              :href="project.url"
              target="_blank"
              rel="noopener"
              class="border-2 border-black bg-gray-100 p-2 hover:bg-gray-200 transition-all duration-150 cursor-pointer flex-shrink-0 hover:-translate-y-0.5 hover:shadow-pixel-sm"
            >
              <div class="aspect-video bg-gray-300 border border-black flex items-center justify-center mb-2 overflow-hidden">
                <img
                  v-if="project.image"
                  :src="project.image"
                  :alt="project.title"
                  class="w-full h-full object-cover object-top"
                >
                <span v-else class="font-pixel text-sm text-black">{{ project.imagePlaceholder }}</span>
              </div>
              <h3 class="font-pixel text-base font-bold text-black">{{ project.title }}</h3>
            </a>
          </div>
        </div>
      </div>
      <!-- Work - Mobile apps (fixed height, scrollable project list) -->
      <div v-else-if="win.id === 'work-mobile'" class="work-projects-content min-h-0">
        <div class="overflow-y-auto py-2 pr-1 h-[min(576px,calc(100dvh-10rem))]">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              v-for="project in mobileProjects"
              :key="project.id"
              :href="project.url"
              target="_blank"
              rel="noopener"
              class="border-2 border-black bg-gray-100 p-2 hover:bg-gray-200 transition-all duration-150 cursor-pointer flex-shrink-0 hover:-translate-y-0.5 hover:shadow-pixel-sm"
            >
              <div class="aspect-video bg-gray-300 border border-black flex items-center justify-center mb-2 overflow-hidden">
                <img
                  v-if="project.image"
                  :src="project.image"
                  :alt="project.title"
                  class="w-full h-full object-cover object-top"
                >
                <span v-else class="font-pixel text-sm text-black">{{ project.imagePlaceholder }}</span>
              </div>
              <h3 class="font-pixel text-base font-bold text-black">{{ project.title }}</h3>
            </a>
          </div>
        </div>
      </div>
      <!-- Contact -->
      <div v-else-if="win.id === 'contact'" class="contact-content py-4 px-1 sm:px-0">
        <div class="space-y-3 sm:space-y-4 font-pixel text-[clamp(1.125rem,4vw,1.5625rem)]">
          <p class="flex flex-col min-[480px]:flex-row min-[480px]:items-center gap-1 min-[480px]:gap-2">
            <span class="flex items-center gap-2 min-[480px]:contents">
              <img :src="emailIcon" alt="" class="w-6 h-6 object-contain flex-shrink-0" aria-hidden="true">
              <span class="font-bold text-black min-[480px]:w-24">Email:</span>
            </span>
            <a href="mailto:hey@buildwithsaad.com" class="text-black underline hover:no-underline break-all">hey@buildwithsaad.com</a>
          </p>
          <p class="flex flex-col min-[480px]:flex-row min-[480px]:items-center gap-1 min-[480px]:gap-2">
            <span class="flex items-center gap-2 min-[480px]:contents">
              <img :src="linkedinIcon" alt="" class="w-6 h-6 object-contain flex-shrink-0" aria-hidden="true">
              <span class="font-bold text-black min-[480px]:w-24">LinkedIn:</span>
            </span>
            <a href="https://www.linkedin.com/in/saad-ilahi-36a881137/" target="_blank" rel="noopener" class="text-black underline hover:no-underline break-all">linkedin.com/in/saad-ilahi-36a881137</a>
          </p>
          <p class="flex flex-col min-[480px]:flex-row min-[480px]:items-center gap-1 min-[480px]:gap-2">
            <span class="flex items-center gap-2 min-[480px]:contents">
              <img :src="whatsappIcon" alt="" class="w-6 h-6 object-contain flex-shrink-0" aria-hidden="true">
              <span class="font-bold text-black min-[480px]:w-24">WhatsApp:</span>
            </span>
            <a href="https://wa.me/97333601825" target="_blank" rel="noopener" class="text-black underline hover:no-underline">wa.me/97333601825</a>
          </p>
        </div>
      </div>
      <!-- Calculator -->
      <div v-else-if="win.id === 'calculator'" class="calculator-window-content py-2 px-1 flex justify-center">
        <CalculatorView class="w-full max-w-[260px]" />
      </div>
    </Window>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import FolderIcon from './FolderIcon.vue'
import Window from './Window.vue'
import CalculatorView from './CalculatorView.vue'
import trashIcon from '@/assets/trash-icon.png'
import fileIcon from '@/assets/file-icon.png'
import desktopBackground from '@/assets/desktop-bg2-924f1967-5752-4203-b00a-6c23628c74ea.png'
import appleIcon from '@/assets/apple-icon.png'
import emailIcon from '@/assets/email-89121c98-d677-4f98-9e01-43729aeb7eae.png'
import linkedinIcon from '@/assets/linkedin-ed9ead21-e242-46b1-93b2-60f0b4c33013.png'
import whatsappIcon from '@/assets/whatsapp-8a3b1d75-a1fe-451c-8f1f-72053b5f84e7.png'
import calculatorIcon from '@/assets/calculator-a0ce4f6d-1be0-4243-858b-1cbd4c368c28.png'
import webMre from '@/assets/mre.png'
import web1step from '@/assets/1step-web.png'
import webEfmc from '@/assets/efmc.png'
import webDali from '@/assets/dali.png'
import mobileSavur from '@/assets/savur.png'
import mobileFooreo from '@/assets/fooreo.png'
import mobile1stepApp from '@/assets/1step-app.png'
import mobileAssp from '@/assets/assp.png'
import mobileChemsafe from '@/assets/chemsafe.png'
import mobileGainScript from '@/assets/gainscript.png'

const skillsList = ['swift', 'java', 'kotlin', 'android', 'ios', 'laravel', 'vue', 'php', 'swiftui', 'xml', 'html', 'css', 'javascript', 'tailwind', 'mysql', 'firebase']
const skillsDropdownOpen = ref(false)
const skillsMenuRef = ref(null)

const timeText = ref('')
const dateText = ref('')

let clockTimer = null

function pad2(n) {
  return String(n).padStart(2, '0')
}

function updateClock() {
  const d = new Date()
  const hours = d.getHours()
  const minutes = d.getMinutes()
  timeText.value = `${pad2(hours)}:${pad2(minutes)}`

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  dateText.value = `${weekdays[d.getDay()]} ${months[d.getMonth()]} ${d.getDate()}`
}

const windowConfig = {
  intro: { title: 'Intro', width: 855, minHeight: 450 },
  about: { title: 'About', width: 900, minHeight: 720 },
  work: { title: 'Work', width: 1080, minHeight: 450 },
  'work-websites': { title: 'Websites', width: 907, minHeight: 648 },
  'work-mobile': { title: 'Mobile apps', width: 907, minHeight: 648 },
  contact: { title: 'Contact', width: 810, minHeight: 495 },
  calculator: { title: 'Calculator', width: 300, minHeight: 380 },
}

const nextZ = ref(11)
const openWindows = ref([
  {
    id: 'intro',
    title: windowConfig.intro.title,
    width: windowConfig.intro.width,
    minHeight: windowConfig.intro.minHeight,
    position: { x: 116, y: 60 },
    zIndex: 10,
  },
])

const websiteProjects = ref([
  { id: 1, title: 'MRE', image: webMre, url: 'https://mre.co/' },
  { id: 2, title: '1 Step Garage', image: web1step, url: 'https://www.1stepgarage.com/' },
  { id: 3, title: 'EFMC', image: webEfmc, url: 'https://elitefmc.com/' },
  { id: 4, title: 'Dali', image: webDali, url: 'https://dalifloristry.com/' },
])
const mobileProjects = ref([
  { id: 1, title: 'Savur', image: mobileSavur, url: 'https://apps.apple.com/bh/app/savur-offers-discounts/id1543869627' },
  { id: 2, title: 'Fooreo', image: mobileFooreo, url: 'https://apps.apple.com/us/app/fooreo-meal-plans-delivery/id6740744979' },
  { id: 3, title: 'GainScript', image: mobileGainScript, url: 'https://apps.apple.com/us/app/gainscript/id6760768737' },
  { id: 4, title: '1 Step Garage', image: mobile1stepApp, url: 'https://apps.apple.com/us/app/1-step-garage/id1574349321' },
  { id: 5, title: 'ASSP', image: mobileAssp, url: 'https://apps.apple.com/us/app/assp-mec/id6504862797' },
  { id: 6, title: 'Chemsafe', image: mobileChemsafe, url: 'https://apps.apple.com/us/app/chemsafe/id6746088635' },
])

function getNextPosition() {
  const offset = openWindows.value.length * 24
  return { x: 80 + offset, y: 60 + offset }
}

function openWindow(id) {
  if (openWindows.value.some((w) => w.id === id)) {
    bringToFront(id)
    return
  }
  const config = windowConfig[id] || { title: id, width: 400, minHeight: 280 }
  openWindows.value.push({
    id,
    title: config.title,
    width: config.width,
    minHeight: config.minHeight,
    position: getNextPosition(),
    zIndex: nextZ.value++,
  })
}

function closeWindow(id) {
  openWindows.value = openWindows.value.filter((w) => w.id !== id)
}

function bringToFront(id) {
  const win = openWindows.value.find((w) => w.id === id)
  if (!win) return
  win.zIndex = nextZ.value++
}

function onWindowFocus(e) {
  const id = e.detail?.id
  if (id) bringToFront(id)
}

function closeSkillsDropdown(e) {
  if (skillsMenuRef.value && !skillsMenuRef.value.contains(e.target)) {
    skillsDropdownOpen.value = false
  }
}

function openEatWeat() {
  window.open('https://eatweat.com/', '_blank')
}

onMounted(() => {
  window.addEventListener('mac-window-focus', onWindowFocus)
  document.addEventListener('click', closeSkillsDropdown)

  updateClock()
  clockTimer = window.setInterval(updateClock, 1000)
})

onUnmounted(() => {
  window.removeEventListener('mac-window-focus', onWindowFocus)
  document.removeEventListener('click', closeSkillsDropdown)

  if (clockTimer) window.clearInterval(clockTimer)
})
</script>
